import { useState } from 'react';
import { CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { Stripe, StripeCardNumberElement } from '@stripe/stripe-js';
import { billing } from '@/services/billing';
import { IS_STRIPE_TEST_MODE } from '@/services/billing/stripe/elements';
import type { SetupIntentPayload } from '@/services/billing/types';
import { sentryCaptureException } from '@/utils/sentry';
import { TEST_SETUP_INTENT, type CreditCardFormAddress } from '../utils/credit-card-form-submit';
import { billingFieldErrors } from '../utils/credit-card-form-validation';
import type { AddonCardEntry } from './use-addon-card-entry';
import { useBillingEditGuard } from './use-billing-edit-guard';

export type AddonPayment =
  | { method: 'saved-card' }
  | { method: 'new-card'; setupIntent: SetupIntentPayload; name: string; address: CreditCardFormAddress };

const CARD_INCOMPLETE = 'Enter your card number, expiry date, and CVC.';
const CARD_FAILED = "Your card couldn't be checked. Try again.";

function isNewCardReady(entry: AddonCardEntry): boolean {
  const fieldErrors = Object.keys(billingFieldErrors(entry.name, entry.address));
  if (isCardComplete(entry) && fieldErrors.length === 0) return true;
  entry.onOpenForm();
  entry.onTouchFields(fieldErrors);
  return false;
}

function isCardComplete(entry: AddonCardEntry): boolean {
  return IS_STRIPE_TEST_MODE || entry.isCardComplete;
}

type CardSetupOutcome = { setupIntent?: SetupIntentPayload; error?: string };

// Confirms a SetupIntent rather than only creating a payment method, because that is what the
// account's card-on-file endpoint takes.
async function confirmCardSetup(
  stripe: Stripe | null,
  card: StripeCardNumberElement | null,
  entry: AddonCardEntry,
): Promise<CardSetupOutcome> {
  if (stripe == null || card == null) return { error: CARD_FAILED };
  try {
    const intent = await billing.getSetupIntent.perform();
    const result = await stripe.confirmCardSetup(intent.setup_intent_client_secret, {
      payment_method: {
        card,
        billing_details: { name: entry.name, address: { postal_code: entry.address.zipCode } },
      },
    });
    if (result.error == null) return { setupIntent: { ...result.setupIntent } };
    return { error: result.error.type === 'card_error' ? (result.error.message ?? CARD_FAILED) : CARD_FAILED };
  } catch (error) {
    sentryCaptureException(error);
    return { error: CARD_FAILED };
  }
}

export type AddonPaymentStep = {
  cardError: string | undefined;
  blockReason: string;
  isBlocked: boolean;
  resolve: () => Promise<AddonPayment | null>;
};

export function useAddonPayment(entry: AddonCardEntry): AddonPaymentStep {
  const stripe = useStripe();
  const elements = useElements();
  const editGuard = useBillingEditGuard();
  const [isCardErrorShown, setIsCardErrorShown] = useState(false);
  const [stripeError, setStripeError] = useState<string>();
  const cardError = stripeError ?? (isCardErrorShown && !isCardComplete(entry) ? CARD_INCOMPLETE : undefined);

  const isStripeBlocked = entry.stripeLoad.isError || (!entry.useSavedCard && entry.stripeLoad.isLoading);

  const resolve = async (): Promise<AddonPayment | null> => {
    if (entry.useSavedCard) return { method: 'saved-card' };
    setIsCardErrorShown(true);
    setStripeError(undefined);
    if (!isNewCardReady(entry)) return null;

    const outcome: CardSetupOutcome = IS_STRIPE_TEST_MODE
      ? { setupIntent: TEST_SETUP_INTENT }
      : await confirmCardSetup(stripe, elements?.getElement(CardNumberElement) ?? null, entry);
    setStripeError(outcome.error);
    if (outcome.setupIntent == null) return null;
    return { method: 'new-card', setupIntent: outcome.setupIntent, name: entry.name, address: entry.address };
  };

  return {
    cardError,
    blockReason: editGuard.isDisabled ? editGuard.disabledMessage : '',
    isBlocked: editGuard.isDisabled || isStripeBlocked,
    resolve,
  };
}
